import {AddIcon, ArrowBackIcon, MinusIcon} from '@chakra-ui/icons';
import {Button, HStack, Input, Text} from '@chakra-ui/react';
import {useTranslation} from 'react-i18next';
import {useState} from 'react';
import {useHalState} from '@synako/haligator';
import {Depuis, UniteQuantite} from 'api-types/ffconnect';
import {Unite} from 'api-types/tolede';
import {contextToDepuis, useBuyProductInputCommand} from 'ff-common';
import {useBoutonAchatQuantiteDisplay} from 'ff-common';
import {ContextsComposant} from 'ff-common/src/types/ContextsComposant';
import {State} from '@synako/halitrek';
import {ItemCatalogue2Desc} from 'api-types/ffconnect-hal';

export const BuyProductPanierInput: React.FC<{
  itemCatalogue: State<ItemCatalogue2Desc>;
  quantite: number;
  onQuantite: (quantite: number) => void;
  commande_en: UniteQuantite;
  somethingSubmitting: boolean;
  context: ContextsComposant;
  primaryColor?: string;
  secondaryColor?: string;
  displayBg?: string;
}> = ({
  itemCatalogue,
  commande_en,
  quantite,
  onQuantite,
  somethingSubmitting,
  context,
  primaryColor,
  secondaryColor,
  displayBg,
}) => {
  const [saisie, setSaisie] = useState(false);

  const quantiteMin = 0;
  const stockState = useHalState(
    itemCatalogue.hasOneLink('stock_marketplace')
      ? itemCatalogue.follow('stock_marketplace')
      : undefined,
  );
  const quantiteMax = itemCatalogue.hasOneLink('disponibilite_article')
    ? itemCatalogue.follow('disponibilite_article').getCache()?.data
        .quantite_livrable
    : stockState.data?.stock;

  const commandeEnColis = commande_en === 'colis';
  const colisEntier = itemCatalogue.data.quantite_colis_entier ?? 1;

  const {
    diminuerLaQuantite,
    augmenterLaQuantite,
    debouncedQuantite,
    setDebouncedQuantite,
  } = useBuyProductInputCommand(
    itemCatalogue,
    commandeEnColis,
    colisEntier,
    quantite,
    onQuantite,
  );

  const depuisTracking = contextToDepuis(context);
  const atMax = quantiteMax !== undefined && quantite >= quantiteMax;

  return (
    <HStack
      flexDirection={{base: 'row', md: 'row-reverse'}}
      w='100%'
      transition='all 0.2s ease'
      opacity={somethingSubmitting ? 0.2 : 1.0}
    >
      {saisie ? (
        <SaisieDeQuantite
          quantiteInitiale={
            commandeEnColis
              ? debouncedQuantite / colisEntier
              : debouncedQuantite
          }
          step={
            commandeEnColis ? 1 : itemCatalogue.data.multiple_de_commande_client
          }
          unite={
            commandeEnColis ? 'CO' : itemCatalogue.data.unite_de_commande_client
          }
          quantiteMin={quantiteMin}
          code_article={itemCatalogue.data.code}
          depuis={depuisTracking}
          primaryColor={primaryColor}
          onClose={() => {
            setSaisie(false);
          }}
          onQuantite={(quantiteSaisie) => {
            const nouvelleQuantite = commandeEnColis
              ? quantiteSaisie * colisEntier
              : quantiteSaisie;
            setDebouncedQuantite(nouvelleQuantite);
            onQuantite(nouvelleQuantite);
            setSaisie(false);
          }}
        />
      ) : (
        <HStack w='100%'>
          <Button
            isDisabled={debouncedQuantite <= quantiteMin}
            height='35px'
            width='35px'
            minW='35px'
            bg={primaryColor ?? 'blue.main'}
            color={secondaryColor ?? 'white'}
            _hover={{opacity: 0.85}}
            onClick={diminuerLaQuantite}
          >
            <MinusIcon />
          </Button>
          <BuyDisplay
            itemCatalogue={itemCatalogue}
            commande_en={commande_en}
            onClick={() => {
              setSaisie(true);
            }}
            quantite={debouncedQuantite}
            displayBg={displayBg}
          />
          <Button
            isDisabled={atMax}
            height='35px'
            width='35px'
            minW='35px'
            bg={primaryColor ?? 'blue.main'}
            color={secondaryColor ?? 'white'}
            _hover={{opacity: 0.85}}
            onClick={augmenterLaQuantite}
          >
            {atMax ? <>MAX</> : <AddIcon />}
          </Button>
        </HStack>
      )}
    </HStack>
  );
};

const BuyDisplay: React.FC<{
  itemCatalogue: State<ItemCatalogue2Desc>;
  commande_en: UniteQuantite;
  /** La quantité est obligatoirement en unite_de_commande_client */
  quantite: number;
  onClick: () => void;
  displayBg?: string;
}> = ({itemCatalogue, commande_en, quantite, onClick, displayBg}) => {
  const commandeEnColis = commande_en === 'colis';
  const {quantite_text} = useBoutonAchatQuantiteDisplay({
    quantite,
    commandeEnColis,
    itemCatalogue,
  });

  return (
    <HStack
      textAlign='center'
      fontSize='xs'
      fontWeight='500'
      boxSizing='border-box'
      justify='center'
      w='100%'
      height='35px'
      outline={displayBg ? 'none' : '0.781px solid'}
      outlineColor={displayBg ? undefined : 'gray.50'}
      border={displayBg ? 'none' : '1px solid'}
      borderColor={displayBg ? undefined : 'gray.light'}
      borderRadius='8px'
      bg={displayBg ?? 'gray.75'}
      px='11px'
      py='8px'
      cursor='pointer'
      _hover={
        displayBg ? {bg: displayBg} : {outlineColor: 'blue.main', bg: 'gray.bg'}
      }
      onClick={onClick}
    >
      <Text lineHeight='1.1'>{quantite_text}</Text>
    </HStack>
  );
};

const SaisieDeQuantite: React.FC<{
  /** Unité à afficher */
  unite: Unite;
  /** Ici c'est la quantité exprimée en  */
  quantiteInitiale: number;
  step: number;
  quantiteMin: number;
  code_article: string;
  depuis: Depuis;
  /** Quitter sans appliquer la quantité */
  onClose: () => void;
  /** Une fois que la quantité est validé.
   *
   * Attention ici c'est la quantité saisie donc si colis pas automatiquement convertie en unité de commande client.
   */
  onQuantite: (quantiteSaisie: number) => void;
  primaryColor?: string;
}> = ({
  unite,
  quantiteInitiale,
  step,
  quantiteMin,
  // code_article,
  // depuis,
  onClose,
  onQuantite,
  primaryColor,
}) => {
  const {t} = useTranslation();
  const [quantite, setQuantite] = useState(quantiteInitiale);

  const commitQuantite = () => {
    onQuantite(quantite);
  };
  return (
    <HStack
      w='100%'
      h='100%'
      alignItems='stretch'
      fontSize='xs'
    >
      <Button
        colorScheme='gray'
        bg='gray.bg'
        borderColor='gray'
        onClick={onClose}
      >
        <ArrowBackIcon />
      </Button>
      <HStack
        position='relative'
        border='1px'
        borderColor={primaryColor ?? 'blue.main'}
        borderRadius='8px'
        boxSizing='border-box'
        flex='1'
        maxW={{base: '100%', lg: '13em'}}
        w='100%'
        p='0'
      >
        <Input
          autoFocus={true}
          textAlign='end'
          fontSize='xs'
          fontWeight='500'
          w='45%'
          border='none!important'
          type='number'
          step={step} // De toute façon on fera l'arrondi plus tard
          value={quantite}
          min={0}
          focusBorderColor='#0000'
          p='0'
          h='100%'
          minW='0px'
          onChange={(event) => {
            event.preventDefault();
            setQuantite(Math.max(quantiteMin, parseFloat(event.target.value)));
          }}
          onBlur={onClose}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === 'Tab') {
              commitQuantite();
            } else if (event.key === 'Escape') {
              onClose();
            }
          }}
        />
        <Text
          textAlign='end'
          alignSelf='50%'
        >
          {t(`unite.${unite}`, {count: quantite})}
        </Text>
      </HStack>
      <Button
        {...(primaryColor
          ? {bg: primaryColor, color: 'white', _hover: {opacity: 0.85}}
          : {colorScheme: 'blue'})}
        onMouseDown={() => {
          commitQuantite();
        }}
        px='3'
      >
        <Text w='24px'>OK</Text>
      </Button>
    </HStack>
  );
};
